import { gql, useQuery } from '@apollo/client';
import { AlertBanner, Button } from '@apollo/orbit';
import React from 'react';
import { Link } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import { getCertificateStatus } from 'src/app/account/settingsPage/ssoConfigurationCard/samlCertificatesList/samlCertHelpers';
import { usePermissions } from 'src/components/permissionGuards/usePermissions';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import type {
  SamlCertBannerQuery,
  SamlCertBannerQueryVariables,
} from 'src/lib/graphqlTypes/types';
import { setOverlayVisibleLocation } from 'src/lib/routing';

const samlCertBannerQuery = gql<
  SamlCertBannerQuery,
  SamlCertBannerQueryVariables
>/* GraphQL */ `
  query SamlCertBannerQuery($accountId: ID!) {
    account(id: $accountId) {
      id
      ssoV2 {
        currentConnection {
          id
          __typename
          ... on SamlConnection {
            metadata {
              verificationCerts {
                id
                notAfter
                notBefore
              }
            }
          }
        }
      }
    }
  }
`;

export const SamlCertBanner = ({ accountId }: { accountId: string }) => {
  const { data } = useQuery(samlCertBannerQuery, {
    variables: { accountId },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const { data: permissionsData } = usePermissions(
    accountId,
    undefined,
    undefined,
  );
  const canProvisionSSO =
    !!permissionsData?.accountPermissions?.canProvisionSSO;

  const [dismissedWarnings, setDismissedWarnings] = useLocalStorage(
    'hasDismissedSamlCertExpiringSoonBanner',
  );
  const [dismissedErrors, setDismissedErrors] = useLocalStorage(
    'hasDismissedSamlCertExpiredBanner',
  );

  const connection = data?.account?.ssoV2?.currentConnection;
  if (connection?.__typename !== 'SamlConnection') return null;

  const verificationCerts = connection.metadata.verificationCerts;

  const hasActive = verificationCerts.some(
    (c) => getCertificateStatus(c) === 'active',
  );
  const hasExpiringSoon = verificationCerts.some(
    (c) => getCertificateStatus(c) === 'expiring soon',
  );
  const hasExpired = verificationCerts.some(
    (c) => getCertificateStatus(c) === 'expired',
  );

  const showWarning = hasExpiringSoon && !hasActive;
  const showError = hasExpired && !hasActive && !hasExpiringSoon;

  if (!showWarning && !showError) return null;

  if (showWarning) {
    return (
      <AlertBanner
        status="warning"
        isDismissible={!canProvisionSSO}
        isOpen={canProvisionSSO || !dismissedWarnings[accountId]}
        onDismiss={() =>
          setDismissedWarnings({ ...dismissedWarnings, [accountId]: true })
        }
        // This is a workaround to center the icon in the alert banner because the content is placed as align start,
        // and the button causes the content to be taller than if it didn't exist.
        className="[&>div]:items-center"
      >
        <div className="flex w-full items-center justify-between">
          <span>
            {canProvisionSSO
              ? 'One or more SSO certificates are expiring soon. Rotate them before expiry to avoid sign-in disruptions.'
              : "One or more of your organization's SSO certificates are expiring soon. Contact your org admin to rotate them before expiry to avoid sign-in disruptions."}
          </span>
          {canProvisionSSO && (
            <Button
              variant="secondary"
              size="sm"
              as={Link}
              to={setOverlayVisibleLocation({
                location: orgRoutes.settings.location({
                  orgId: accountId,
                  tab: 'security',
                }),
                overlayName: Config.modals.uploadNewCertificate,
              })}
              className="border-warning bg-warning text-warning hover:bg-warning-hover hover:text-inverted"
            >
              Manage certificates
            </Button>
          )}
        </div>
      </AlertBanner>
    );
  }

  return (
    <AlertBanner
      status="error"
      isDismissible={!canProvisionSSO}
      isOpen={canProvisionSSO || !dismissedErrors[accountId]}
      onDismiss={() =>
        setDismissedErrors({ ...dismissedErrors, [accountId]: true })
      }
      // This is a workaround to center the icon in the alert banner because the content is placed as align start,
      // and the button causes the content to be taller than if it didn't exist.
      className="[&>div]:items-center"
    >
      <div className="flex w-full items-center justify-between">
        <span>
          {canProvisionSSO
            ? 'A certificate has expired and may be causing sign-in failures.'
            : "One or more of your organization's SSO certificates have expired and may be causing sign-in failures. Contact your org admin to update them."}
        </span>
        {canProvisionSSO && (
          <Button
            variant="secondary"
            size="sm"
            as={Link}
            to={setOverlayVisibleLocation({
              location: orgRoutes.settings.location({
                orgId: accountId,
                tab: 'security',
              }),
              overlayName: Config.modals.uploadNewCertificate,
            })}
            className="border-error bg-error text-error hover:bg-error-hover hover:text-inverted"
          >
            Manage certificates
          </Button>
        )}
      </div>
    </AlertBanner>
  );
};
